import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Edit2, Trash2, Percent, DollarSign, Calendar, Tag, Package, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Sidebar from '@/components/Sidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

export default function DiscountsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const [discounts, setDiscounts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    discount_type: 'percentage',
    value: '',
    auto_apply: false,
    days_of_week: [],
    applicable_packages: [],
    applicable_addons: [],
    tags: [],
    start_date: '',
    end_date: '',
    is_active: true
  });
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [discountsRes, packagesRes, addonsRes] = await Promise.all([
        axios.get(`${API}/discounts`),
        axios.get(`${API}/packages`),
        axios.get(`${API}/addons`)
      ]);
      setDiscounts(discountsRes.data);
      setPackages(packagesRes.data);
      setAddons(addonsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      discount_type: 'percentage',
      value: '',
      auto_apply: false,
      days_of_week: [],
      applicable_packages: [],
      applicable_addons: [],
      tags: [],
      start_date: '',
      end_date: '',
      is_active: true
    });
    setEditingDiscount(null);
    setNewTag('');
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (discount) => {
    setEditingDiscount(discount);
    setFormData({
      name: discount.name || '',
      description: discount.description || '',
      discount_type: discount.discount_type || 'percentage',
      value: discount.value?.toString() || '',
      auto_apply: discount.auto_apply || false,
      days_of_week: discount.days_of_week || [],
      applicable_packages: discount.applicable_packages || [],
      applicable_addons: discount.applicable_addons || [],
      tags: discount.tags || [],
      start_date: discount.start_date || '',
      end_date: discount.end_date || '',
      is_active: discount.is_active !== false
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Discount name is required');
      return;
    }
    if (!formData.value || parseFloat(formData.value) <= 0) {
      toast.error('Please enter a valid discount value');
      return;
    }

    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value)
      };

      if (editingDiscount) {
        await axios.put(`${API}/discounts/${editingDiscount.id}`, payload);
        toast.success('Discount updated successfully');
      } else {
        await axios.post(`${API}/discounts`, payload);
        toast.success('Discount created successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save discount');
    }
  };

  const handleDelete = async (discountId) => {
    if (!window.confirm('Are you sure you want to delete this discount?')) return;
    
    try {
      await axios.delete(`${API}/discounts/${discountId}`);
      toast.success('Discount deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete discount');
    }
  };

  const toggleDayOfWeek = (day) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  const togglePackage = (packageId) => {
    setFormData(prev => ({
      ...prev,
      applicable_packages: prev.applicable_packages.includes(packageId)
        ? prev.applicable_packages.filter(p => p !== packageId)
        : [...prev.applicable_packages, packageId]
    }));
  };

  const toggleAddon = (addonId) => {
    setFormData(prev => ({
      ...prev,
      applicable_addons: prev.applicable_addons.includes(addonId)
        ? prev.applicable_addons.filter(a => a !== addonId)
        : [...prev.applicable_addons, addonId]
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  const formatDiscountValue = (discount) => {
    if (discount.discount_type === 'percentage') {
      return `${discount.value}%`;
    }
    return `$${discount.value.toFixed(2)}`;
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo} />
      
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
                <Percent className="w-8 h-8 text-neon-purple" />
                Discounts
              </h1>
              <p className="text-muted-foreground mt-1">Manage pricing discounts and promotions</p>
            </div>
            <Button onClick={openCreateDialog} className="bg-neon-purple hover:bg-neon-purple/80" data-testid="create-discount-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Discount
            </Button>
          </div>

          {/* Discounts Grid */}
          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : discounts.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-2xl">
              <Percent className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No discounts created yet</p>
              <p className="text-sm text-muted-foreground mt-1">Create discounts to offer special pricing</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {discounts.map((discount) => (
                <div
                  key={discount.id}
                  className={`glass-card rounded-xl p-5 border ${discount.is_active ? 'border-neon-purple/30' : 'border-gray-500/30 opacity-60'}`}
                  data-testid={`discount-card-${discount.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`p-2 rounded-lg ${discount.discount_type === 'percentage' ? 'bg-neon-purple/20' : 'bg-green-500/20'}`}>
                        {discount.discount_type === 'percentage' ? (
                          <Percent className="w-5 h-5 text-neon-purple" />
                        ) : (
                          <DollarSign className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold">{discount.name}</h3>
                        <span className={`text-lg font-bold ${discount.discount_type === 'percentage' ? 'text-neon-purple' : 'text-green-500'}`}>
                          {formatDiscountValue(discount)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEditDialog(discount)} data-testid={`edit-discount-${discount.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(discount.id)} className="text-red-500 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {discount.description && (
                    <p className="text-sm text-muted-foreground mb-3">{discount.description}</p>
                  )}

                  <div className="space-y-2">
                    {discount.auto_apply && (
                      <Badge className="bg-green-500/20 text-green-500 border-0">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Auto-Apply
                      </Badge>
                    )}
                    
                    {discount.days_of_week?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {discount.days_of_week.map(day => (
                          <Badge key={day} variant="outline" className="text-xs capitalize">
                            {day.slice(0, 3)}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {discount.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {discount.tags.map(tag => (
                          <Badge key={tag} className="bg-blue-500/20 text-blue-400 border-0 text-xs">
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {(discount.start_date || discount.end_date) && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-2">
                        <Calendar className="w-3 h-3" />
                        {discount.start_date && `From ${discount.start_date}`}
                        {discount.start_date && discount.end_date && ' - '}
                        {discount.end_date && `Until ${discount.end_date}`}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="glass-card border-glass-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="w-5 h-5 text-neon-purple" />
              {editingDiscount ? 'Edit Discount' : 'Create Discount'}
            </DialogTitle>
            <DialogDescription>
              Set up a discount with optional auto-apply rules and contract tags
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Early Bird Special"
                  className="bg-muted/20 border-glass-border"
                  data-testid="discount-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                  className="bg-muted/20 border-glass-border"
                />
              </div>
            </div>

            {/* Type and Value */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select value={formData.discount_type} onValueChange={(v) => setFormData({ ...formData, discount_type: v })}>
                  <SelectTrigger className="bg-muted/20 border-glass-border" data-testid="discount-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">
                      <span className="flex items-center gap-2">
                        <Percent className="w-4 h-4" /> Percentage
                      </span>
                    </SelectItem>
                    <SelectItem value="fixed">
                      <span className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4" /> Fixed Amount
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Value *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {formData.discount_type === 'percentage' ? '%' : '$'}
                  </span>
                  <Input
                    type="number"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    placeholder={formData.discount_type === 'percentage' ? '10' : '100'}
                    className="bg-muted/20 border-glass-border pl-8"
                    min="0"
                    max={formData.discount_type === 'percentage' ? '100' : undefined}
                    data-testid="discount-value-input"
                  />
                </div>
              </div>
            </div>

            {/* Auto Apply Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-green-500" />
                <div>
                  <Label className="text-green-500">Auto-Apply Discount</Label>
                  <p className="text-xs text-muted-foreground">Automatically apply when conditions match</p>
                </div>
              </div>
              <Switch
                checked={formData.auto_apply}
                onCheckedChange={(checked) => setFormData({ ...formData, auto_apply: checked })}
                data-testid="auto-apply-switch"
              />
            </div>

            {/* Days of Week */}
            <div className="space-y-2">
              <Label>Days of Week (leave empty for all days)</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map(day => (
                  <Badge
                    key={day.value}
                    variant={formData.days_of_week.includes(day.value) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-colors ${
                      formData.days_of_week.includes(day.value) 
                        ? 'bg-neon-purple text-white' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => toggleDayOfWeek(day.value)}
                  >
                    {day.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Applicable Packages */}
            {packages.length > 0 && (
              <div className="space-y-2">
                <Label>Applicable Packages (leave empty for all packages)</Label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 rounded-lg bg-muted/10">
                  {packages.map(pkg => (
                    <div key={pkg.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.applicable_packages.includes(pkg.id)}
                        onCheckedChange={() => togglePackage(pkg.id)}
                      />
                      <span className="text-sm">{pkg.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Applicable Add-ons */}
            {addons.length > 0 && (
              <div className="space-y-2">
                <Label>Applicable Add-ons (leave empty for all add-ons)</Label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 rounded-lg bg-muted/10">
                  {addons.map(addon => (
                    <div key={addon.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.applicable_addons.includes(addon.id)}
                        onCheckedChange={() => toggleAddon(addon.id)}
                      />
                      <span className="text-sm">{addon.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="bg-muted/20 border-glass-border"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date (optional)</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="bg-muted/20 border-glass-border"
                />
              </div>
            </div>

            {/* Tags for Contracts */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-blue-400" />
                Contract Tags
              </Label>
              <p className="text-xs text-muted-foreground">Tags will be displayed on contracts when this discount is applied</p>
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Add a tag (e.g., Early Booking)"
                  className="bg-muted/20 border-glass-border"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map(tag => (
                    <Badge key={tag} className="bg-blue-500/20 text-blue-400 border-0">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="ml-2 hover:text-red-400">&times;</button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label>Discount is active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-neon-purple hover:bg-neon-purple/80" data-testid="save-discount-btn">
              {editingDiscount ? 'Update Discount' : 'Create Discount'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
